import { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, User, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/track";

const DEFAULT_SUGGESTIONS = [
  "Where do academic and community oncologists disagree about elacestrant, and why?",
  "How does the SERENA-6 pre-progression paradigm threaten ORSERDU's position?",
];

export default function AIConsole({ role, prefill, onPrefillConsumed }) {
  const suggestions = [...(role?.aiSuggestions || []), ...DEFAULT_SUGGESTIONS].slice(0, 4);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (prefill && !streaming) {
      const q = prefill;
      onPrefillConsumed?.();
      ask(`${q} Provide a deeper analysis than the standard synthesis: quantify positions, contrast segments and regions, and end with prioritized recommendations.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const ask = async (question) => {
    if (!question.trim() || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API_BASE}/ai/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, session_id: sessionId, role: role?.label }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.type === "session") setSessionId(ev.session_id);
          else if (ev.type === "delta") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + ev.content };
              return copy;
            });
          } else if (ev.type === "error") {
            toast.error("AI synthesis failed: " + ev.message);
          }
        }
      }
      track("ai_brief_generated", "/ai", { question_length: question.length });
    } catch (e) {
      toast.error("Connection to AI console failed");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="ai-console-view">
      <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-teal-400" />
          <h3 className="font-heading text-lg font-bold text-white">AI Intelligence Console</h3>
        </div>
        <p className="text-sm text-slate-400 mt-1">Ask the ORSERDU intelligence dataset. Answers are source-linked, calibrated and tailored to your role lens{role ? ` (${role.label})` : ""}. Powered by GPT-5.4.</p>
      </div>

      {messages.length === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {suggestions.map((s, i) => (
            <button key={s} data-testid={`ai-suggestion-${i}`} onClick={() => ask(s)}
              className="text-left text-sm text-slate-700 bg-white border border-slate-200/80 rounded-xl p-4 hover:border-teal-400 hover:bg-teal-50/40 transition-colors shadow-sm">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-3" data-testid="ai-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && (
              <div className="w-8 h-8 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center shrink-0">
                {streaming && i === messages.length - 1 && !m.content ? <Loader2 className="w-4 h-4 text-teal-600 animate-spin" /> : <Sparkles className="w-4 h-4 text-teal-600" />}
              </div>
            )}
            <div className={`max-w-[85%] ${m.role === "user" ? "" : "flex-1"}`}>
              <div className={`rounded-xl px-4 py-3 text-sm leading-relaxed ${
                m.role === "user" ? "bg-slate-900 text-white whitespace-pre-wrap" : "bg-white border border-slate-200/80 text-slate-800 shadow-sm"
              }`}>
                {m.role === "assistant" ? (
                  m.content ? (
                    <div className="prose-sm ai-markdown">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    </div>
                  ) : "Synthesizing intelligence…"
                ) : m.content}
              </div>
              {m.role === "assistant" && m.content && (
                <span className="inline-block mt-1 text-[10px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5" data-testid="ai-derived-label">
                  AI-derived interpretation
                </span>
              )}
            </div>
            {m.role === "user" && (
              <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                <User className="w-4 h-4 text-slate-500" />
              </div>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2 items-end sticky bottom-4">
        <Textarea
          data-testid="ai-question-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(input); } }}
          placeholder="Ask about HCP views, consensus, evidence gaps, strategy…"
          className="min-h-[52px] max-h-32 bg-white border-slate-200 rounded-xl focus-visible:ring-teal-500 resize-none"
        />
        <Button data-testid="ai-send-btn" onClick={() => ask(input)} disabled={streaming || !input.trim()}
          className="h-[52px] px-5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
